import './LeaderboardLaSelection.css';
import React, { useState, useEffect, useMemo, useRef } from "react"
import { useLocation } from 'react-router-dom';
import logo from './logo.png';
import {
    enrichWithPreviousLeaderboard,
    fetchUnifiedLeaderboardData,
    parseExcludedSessionIds,
} from '../../shared/leaderboardShared';

const PAGE_SIZE = 20;
const COLUMN_SIZE = 10;

const Row = React.memo(function Row({ rank, teamname, points, elims, avg_place, wins, games, order, onClick, positionChange, refreshDelta, showPositionIndicators, animationEnabled, hasPositionChanged, cascadeFadeEnabled, cascadeIndex, alive, showFlags, memberData }) {
    const renderPositionChange = () => {
        if (!showPositionIndicators || alive || games < 2 || positionChange === null) {
            return null;
        }

        const getIndicatorStyle = (type, value) => {
            const textLength = String(value).length;
            let baseWidth, fontSize, padding;
            if (textLength === 1) {
                baseWidth = 20;
                fontSize = 11;
                padding = '2px 6px';
            } else if (textLength === 2) {
                baseWidth = 26;
                fontSize = 10;
                padding = '2px 5px';
            } else if (textLength === 3) {
                baseWidth = 32;
                fontSize = 9;
                padding = '2px 4px';
            } else {
                baseWidth = 38;
                fontSize = 8;
                padding = '2px 3px';
            }

            const baseStyle = {
                padding: padding,
                borderRadius: '3px',
                fontSize: `${fontSize}px`,
                fontWeight: 'bold',
                border: '1px solid',
                minWidth: `${baseWidth}px`,
                textAlign: 'center',
                display: 'inline-block',
                marginLeft: '0px',
                position: 'absolute',
                right: '-36px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
            };

            if (type === 'neutral') {
                return {
                    ...baseStyle,
                    backgroundColor: '#666',
                    color: '#fff',
                    borderColor: '#666'
                };
            } else if (type === 'positive') {
                return {
                    ...baseStyle,
                    backgroundColor: '#4CAF50',
                    color: '#fff',
                    borderColor: '#4CAF50'
                };
            } else {
                return {
                    ...baseStyle,
                    backgroundColor: '#f44336',
                    color: '#fff',
                    borderColor: '#f44336'
                };
            }
        };

        if (positionChange === 0) {
            return <span className="position_change neutral" style={getIndicatorStyle('neutral', '=')}>=</span>;
        }
        if (positionChange > 0) {
            return <span className="position_change positive" style={getIndicatorStyle('positive', `+${positionChange}`)}>+{positionChange}</span>;
        } else {
            return <span className="position_change negative" style={getIndicatorStyle('negative', positionChange)}>{positionChange}</span>;
        }
    };

    const getAnimationStyle = () => {
        if (!animationEnabled || !hasPositionChanged || !refreshDelta) return {};

        const rowHeight = 70;
        const realDistance = Math.abs(refreshDelta) * rowHeight;

        const baseSpeed = 200;
        const minDuration = 0.6;
        const maxDuration = 2.0;

        let calculatedDuration = realDistance / baseSpeed;
        calculatedDuration = Math.max(minDuration, Math.min(maxDuration, calculatedDuration));

        const fromPosition = refreshDelta > 0 ? realDistance : -realDistance;

        return {
            '--slide-from': `${fromPosition}px`,
            '--slide-to': '0px',
            animation: `slideFromTo ${calculatedDuration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            zIndex: 10
        };
    };

    return (
        <div className='row_container' style={{
            '--animation-order': order,
            opacity: cascadeFadeEnabled ? 0 : (animationEnabled && hasPositionChanged ? 0.9 : 1),
            animation: cascadeFadeEnabled ? 'fadeIn 0.8s forwards' : 'none',
            animationDelay: cascadeFadeEnabled ? `${cascadeIndex * 0.1}s` : '0s',
            transition: animationEnabled && hasPositionChanged ? 'none' : 'opacity 0.3s ease',
            ...getAnimationStyle()
        }}>
            <div className='rank_container' style={{
                fontSize: rank >= 100 ? '20px' : '24px'
            }}>
                {rank}
                {renderPositionChange()}
            </div>
            <div className='name_container' style={{
                cursor: 'pointer',
                fontSize: teamname.length > 40 ? '11px' : teamname.length > 35 ? '12px' : teamname.length > 30 ? '13px' : teamname.length > 25 ? '15px' : teamname.length > 20 ? '17px' : teamname.length > 15 ? '19px' : '22px',
                whiteSpace: 'nowrap'
            }} onClick={onClick}>
                {alive && <span className='alive-dot' />}
                {showFlags && memberData && memberData.length > 0 ? (
                    memberData.map((member, idx) => (
                        <span key={idx} className='member_with_flag'>
                            <img
                                src={member.flagUrl}
                                alt="flag"
                                className='flag_icon'
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = member.fallbackFlagUrl;
                                }}
                            />
                            <span>{member.name}</span>
                            {idx < memberData.length - 1 && <span className='separator'> - </span>}
                        </span>
                    ))
                ) : (
                    teamname
                )}
            </div>
            <div className='info_box'>{avg_place.toFixed(2)}</div>
            <div className='info_box'>{elims}</div>
            <div className='info_box'>{wins}</div>
            <div className='info_box points_box'>{points}</div>
        </div>
    );
}, (prevProps, nextProps) => {

    return (
        prevProps.rank === nextProps.rank &&
        prevProps.teamname === nextProps.teamname &&
        prevProps.points === nextProps.points &&
        prevProps.elims === nextProps.elims &&
        Math.abs(prevProps.avg_place - nextProps.avg_place) < 0.01 &&
        prevProps.wins === nextProps.wins &&
        prevProps.games === nextProps.games &&
        prevProps.positionChange === nextProps.positionChange &&
        prevProps.refreshDelta === nextProps.refreshDelta &&
        prevProps.showPositionIndicators === nextProps.showPositionIndicators &&
        prevProps.animationEnabled === nextProps.animationEnabled &&
        prevProps.hasPositionChanged === nextProps.hasPositionChanged &&
        prevProps.cascadeFadeEnabled === nextProps.cascadeFadeEnabled &&
        prevProps.alive === nextProps.alive &&
        prevProps.showFlags === nextProps.showFlags &&
        prevProps.memberData === nextProps.memberData
    );
});

function LeaderboardLaSelection() {

    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const lobby1Id = urlParams.get('id1') || urlParams.get('id');
    const lobby2Id = urlParams.get('id2');
    const finaleId = urlParams.get('id3') || urlParams.get('idfinale');
    const cascadeParam = urlParams.get('cascade');
    const flagsParam = urlParams.get('flags');
    const excludedSessionIds = useMemo(
        () => parseExcludedSessionIds(new URLSearchParams(location.search)),
        [location.search]
    );
    const excludedSessionIdsKey = useMemo(
        () => Array.from(excludedSessionIds).sort().join(','),
        [excludedSessionIds]
    );

    const [activeLobby, setActiveLobby] = useState(1);
    const leaderboard_id = activeLobby === 3 ? finaleId : activeLobby === 2 ? lobby2Id : lobby1Id;

    const [leaderboard, setLeaderboard] = useState([]);
    const [apiPage, setApiPage] = useState(0);
    const [localPage, setLocalPage] = useState(0);
    const [totalApiPages, setTotalApiPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFlags, setShowFlags] = useState(flagsParam === 'true');
    const [showSearch, setShowSearch] = useState(false);


    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamDetails, setTeamDetails] = useState({});
    const [lastChangeTime, setLastChangeTime] = useState(Date.now());
    const [showPositionIndicators, setShowPositionIndicators] = useState(false);
    const [hasRefreshedOnce, setHasRefreshedOnce] = useState(false);
    const [animationEnabled, setAnimationEnabled] = useState(false);
    const [cascadeFadeEnabled, setCascadeFadeEnabled] = useState(cascadeParam === 'true');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const wasAllDeadRef = useRef(false);
    const previousLeaderboardRef = useRef(null);


    const loadLeaderboard = async () => {
        try {
            const data = await fetchUnifiedLeaderboardData({
                leaderboardId: leaderboard_id,
                excludedSessionIds,
                showFlags,
                forceRankByPoints: true,
                includeV7: true,
                indicatorsOnlyWhenAllDead: true,
            });

            setTotalApiPages(data.totalPages);
            setShowPositionIndicators(data.showPositionIndicators);
            setHasRefreshedOnce(true);
            wasAllDeadRef.current = data.allDead;

            const previousLeaderboard = previousLeaderboardRef.current;
            const merged = enrichWithPreviousLeaderboard(data.leaderboard, previousLeaderboard);
            const previousPlaces = new Map(
                (previousLeaderboard || []).map((team) => [team.teamname, team.place])
            );
            const withRefreshDeltas = merged.leaderboard.map((team) => {
                const previousPlace = previousPlaces.get(team.teamname);
                return {
                    ...team,
                    refreshDelta: typeof previousPlace === 'number' ? previousPlace - team.place : 0,
                };
            });
            setLeaderboard(withRefreshDeltas);
            setTeamDetails(data.teamDetails);
            previousLeaderboardRef.current = withRefreshDeltas;

            if (isInitialLoad) {
                setIsInitialLoad(false);
            }

            if (previousLeaderboard && merged.changedCount > 0) {
                setLastChangeTime(Date.now());
                setAnimationEnabled(true);
                setTimeout(() => { setAnimationEnabled(false); }, 2500);
            } else {
                setAnimationEnabled(false);
            }
        } catch (error) {
            console.error('Error loading leaderboard data:', error);
        }
    };

    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'F8') {
                event.preventDefault();
                setCascadeFadeEnabled(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);

    useEffect(() => {
        loadLeaderboard();

        const interval = setInterval(loadLeaderboard, 15000);

        return () => clearInterval(interval);
    }, [leaderboard_id, showFlags, excludedSessionIdsKey]);

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'F1') {
                event.preventDefault();
                setShowSearch(prev => !prev);
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        setLocalPage(0);
    }, [searchQuery]);



    const switchLobby = (lobby) => {
        if (lobby === activeLobby) return;
        setActiveLobby(lobby);
        setLocalPage(0);
        setLeaderboard([]);
        setTeamDetails({});
        previousLeaderboardRef.current = null;
        setIsInitialLoad(true);
        setAnimationEnabled(false);
        setSelectedTeam(null);
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'F2') {
                event.preventDefault();
                const availableLobbies = [
                    ...(lobby1Id ? [1] : []),
                    ...(lobby2Id ? [2] : []),
                    ...(finaleId ? [3] : []),
                ];
                if (availableLobbies.length < 2) return;
                const currentIndex = availableLobbies.indexOf(activeLobby);
                switchLobby(availableLobbies[(currentIndex + 1) % availableLobbies.length]);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeLobby, lobby1Id, lobby2Id, finaleId]);

    const handleTeamClick = (teamname) => {
        setSelectedTeam(teamname);
    };

    const closeModal = () => {
        setSelectedTeam(null);
    };


    const filterLeaderboard = (teams) => teams.filter(team => {
        if (team.teamname.toLowerCase().includes(searchQuery.toLowerCase())) {
            return true;
        }
        if (!isNaN(searchQuery) && searchQuery.trim() !== '') {
            const searchPosition = parseInt(searchQuery.trim());
            if (team.place === searchPosition) {
                return true;
            }
        }
        if (teamDetails[team.teamname] && teamDetails[team.teamname].members) {
            return teamDetails[team.teamname].members.some(member =>
                member.ingame_name && member.ingame_name.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        return false;
    });

    function nextPage() {
        const maxPages = Math.ceil(filterLeaderboard(leaderboard).length / PAGE_SIZE) - 1;

        if (localPage < maxPages) {
            setLocalPage(localPage + 1);
        }
    }

    function previousPage() {
        if (localPage > 0) {
            setLocalPage(localPage - 1);
        }
    }

    const filteredLeaderboard = filterLeaderboard(leaderboard);
    const startIndex = localPage * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const displayedLeaderboard = filteredLeaderboard.slice(startIndex, endIndex);
    const leftColumn = displayedLeaderboard.slice(0, COLUMN_SIZE);
    const rightColumn = displayedLeaderboard.slice(COLUMN_SIZE, PAGE_SIZE);

    const renderHeader = () => (
        <div className='header_container'>
            <div className='rank_header' onClick={previousPage}>PLACE</div>
            <div className='name_header'>ÉQUIPE</div>
            <div className='info_header avg_place_header'>AVG PLACE</div>
            <div className='info_header'>ELIMS</div>
            <div className='info_header'>WINS</div>
            <div className='info_header' onClick={nextPage}>POINTS</div>
        </div>
    );

    const renderRows = (rows, cascadeOffset) => rows.map((data, index) => {
        const movedDistance = Math.abs(data.refreshDelta || 0);
        let animationOrder;

        if (movedDistance >= 500) {
            animationOrder = 1;
        } else if (movedDistance >= 100) {
            animationOrder = 2;
        } else if (movedDistance >= 50) {
            animationOrder = 3;
        } else if (movedDistance >= 10) {
            animationOrder = 4;
        } else if (movedDistance > 0) {
            animationOrder = 5;
        } else {
            animationOrder = cascadeOffset + index + 6;
        }

        return (
            <Row
                key={`${data.teamId || data.teamname}-${data.place}`}
                rank={data.place}
                teamname={data.teamname}
                points={data.points}
                elims={data.elims}
                wins={data.wins}
                games={data.games}
                avg_place={data.avg_place}
                order={animationOrder}
                alive={data.alive}
                onClick={() => handleTeamClick(data.teamname)}
                positionChange={data.positionChange || 0}
                refreshDelta={data.refreshDelta || 0}
                showPositionIndicators={showPositionIndicators}
                animationEnabled={animationEnabled && data.hasPositionChanged}
                hasPositionChanged={data.hasPositionChanged || false}
                cascadeFadeEnabled={cascadeFadeEnabled}
                cascadeIndex={cascadeOffset + index}
                showFlags={showFlags}
                memberData={data.memberData}
            />
        );
    });

    return (
        <div className='la-selection'>

            {showSearch && (
                <div className='search_container'>
                    <input
                        type="text"
                        placeholder="Rechercher un joueur"
                        className="search_input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            )}

            <div className='leaderboard_container'>
                <div className='logo_container'>
                    <img src={logo} alt="La Sélection" className='logo_image' />
                </div>

                <div className='lobby_buttons'>
                    <button
                        className={`lobby_button ${activeLobby === 1 ? 'active' : ''}`}
                        onClick={() => switchLobby(1)}
                    >Classement Lobby 1</button>
                    <button
                        className={`lobby_button ${activeLobby === 2 ? 'active' : ''}`}
                        onClick={() => switchLobby(2)}
                    >Classement Lobby 2</button>
                    <button
                        className={`lobby_button ${activeLobby === 3 ? 'active' : ''}`}
                        onClick={() => switchLobby(3)}
                    >Classement Finale</button>
                </div>

                <div className='dual_leaderboard' key={`lobby-${activeLobby}`}>
                    <div className='leaderboard_column'>
                        {renderHeader()}
                        {renderRows(leftColumn, 0)}
                    </div>
                    {rightColumn.length > 0 && (
                        <div className='leaderboard_column second-column'>
                            {renderHeader()}
                            {renderRows(rightColumn, COLUMN_SIZE)}
                        </div>
                    )}
                </div>

            </div>



            {selectedTeam && teamDetails[selectedTeam] && (
                <div className='modal_overlay' onClick={closeModal}>
                    <div className='modal_content' onClick={(e) => e.stopPropagation()}>
                        <div className='modal_header'>
                            <h2>Stats détaillées - {selectedTeam}</h2>
                            <button className='close_button' onClick={closeModal}>×</button>
                        </div>
                        <div className='modal_body'>
                            <div className='team_summary'>
                                <h3>Résumé de l'équipe :</h3>
                                <div className='stats_grid'>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Position:</span>
                                        <span className='stat_value'>#{teamDetails[selectedTeam].teamData.place}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Points totaux:</span>
                                        <span className='stat_value'>{teamDetails[selectedTeam].teamData.points}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Parties jouées :</span>
                                        <span className='stat_value'>{teamDetails[selectedTeam].sessions.length}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Victoires:</span>
                                        <span className='stat_value'>{teamDetails[selectedTeam].sessions.filter(s => s.place === 1).length}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Top 3:</span>
                                        <span className='stat_value'>{teamDetails[selectedTeam].sessions.filter(s => s.place <= 3).length}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Élims totales:</span>
                                        <span className='stat_value'>{teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.kills, 0)}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Place moyenne:</span>
                                        <span className='stat_value'>{(teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.place, 0) / teamDetails[selectedTeam].sessions.length).toFixed(2)}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Élims/partie:</span>
                                        <span className='stat_value'>{(teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.kills, 0) / teamDetails[selectedTeam].sessions.length).toFixed(2)}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Meilleure place:</span>
                                        <span className='stat_value'>{Math.min(...teamDetails[selectedTeam].sessions.map(s => s.place))}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Pire place:</span>
                                        <span className='stat_value'>{Math.max(...teamDetails[selectedTeam].sessions.map(s => s.place))}</span>
                                    </div>
                                    <div className='stat_item'>
                                        <span className='stat_label'>Max élims/partie:</span>
                                        <span className='stat_value'>{Math.max(...teamDetails[selectedTeam].sessions.map(s => s.kills))}</span>
                                    </div>

                                </div>
                            </div>

                            <div className='members_section'>
                                <h3>Membre(s) de l'équipe :</h3>
                                <div className='members_grid'>
                                    {teamDetails[selectedTeam].members.map((member, index) => (
                                        <div key={index} className='member_card'>
                                            <strong>{member.name}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className='sessions_section'>
                                <h3>Historique détaillé des games :</h3>
                                <div className='sessions_table'>
                                    <div className='session_header'>
                                        <div>Game</div>
                                        <div>Place</div>
                                        <div>Éliminations</div>
                                    </div>
                                    {teamDetails[selectedTeam].sessions.map((session, index) => (
                                        <div key={index} className='session_row'>
                                            <div className='session_highlight'>{index + 1}</div>
                                            <div className={`place_${session.place <= 3 ? 'top' : session.place <= 10 ? 'good' : 'normal'}`}>{session.place}</div>
                                            <div className='session_highlight'>{session.kills}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LeaderboardLaSelection
